// "Dojo Mode" ambient audio — procedurally synthesized with the Web Audio API.
// Royalty-free by construction (no external track). A calm Japanese garden bed:
//   • trickling water — a soft filtered-noise brook plus random droplet plinks
//   • flute — breathy, vibrato sustained notes phrased on a major pentatonic
//   • koto — gently plucked strings in the same scale
// Site-wide singleton with 2s fade in/out, a duck mode for the Course Player,
// and localStorage persistence.
import { useEffect, useState } from "react";

const KEY = "learnme_dojo";
const KEY_VOL = "learnme_dojo_vol";
const KEY_DUCK = "learnme_dojo_duck";
const KEY_POS = "learnme_dojo_pos";
const DEFAULT_VOLUME = 0.4;
const DEFAULT_DUCK = 0.15;
const FADE = 2; // seconds
// C5 D5 E5 G5 A5 — major pentatonic (calm, "eastern" feel), used by the koto.
const PENTA = [523.25, 587.33, 659.25, 783.99, 880.0];
// One octave down for the flute's warmer register.
const FLUTE_SCALE = PENTA.map((f) => f / 2);

class DojoAudio {
  constructor() {
    this.ctx = null;
    this.master = null;
    this.synthGain = null;
    this.trackGain = null;
    this.enabled = false;
    this.ducked = false;
    this._timers = [];
    this._listeners = new Set();
    this._resume = null;
    this._noise = null;
    this.trackUrl = null;   // custom uploaded track (null => synthesized ambient)
    this.audioEl = null;
    this.mediaSrc = null;
    this.mode = "synth";    // "synth" | "track"
    this.volume = this._getVol();
    this.duckLevel = this._getDuck();
    this._lastPosSave = 0;
  }

  getPref() { try { return sessionStorage.getItem(KEY) === "on"; } catch { return false; } }
  _setPref(on) { try { sessionStorage.setItem(KEY, on ? "on" : "off"); } catch {} }
  _getVol() {
    try {
      const v = parseFloat(localStorage.getItem(KEY_VOL));
      return Number.isFinite(v) ? Math.min(1, Math.max(0, v)) : DEFAULT_VOLUME;
    } catch { return DEFAULT_VOLUME; }
  }
  _setVol(v) { try { localStorage.setItem(KEY_VOL, String(v)); } catch {} }
  _getDuck() {
    try {
      const v = parseFloat(localStorage.getItem(KEY_DUCK));
      return Number.isFinite(v) ? Math.min(1, Math.max(0, v)) : DEFAULT_DUCK;
    } catch { return DEFAULT_DUCK; }
  }
  _setDuck(v) { try { localStorage.setItem(KEY_DUCK, String(v)); } catch {} }

  subscribe(fn) { this._listeners.add(fn); fn(this.enabled); return () => this._listeners.delete(fn); }
  _emit() { this._listeners.forEach((f) => f(this.enabled)); }

  _ensureCtx() {
    if (this.ctx) return;
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    this.ctx = new AC();
    this.master = this.ctx.createGain();
    this.master.gain.value = 0;
    this.master.connect(this.ctx.destination);
    // Two sub-buses under the master (which owns fade + duck): the synth bed and
    // the custom-track player. We mute one and unmute the other by mode.
    this.synthGain = this.ctx.createGain(); this.synthGain.gain.value = 1;
    this.trackGain = this.ctx.createGain(); this.trackGain.gain.value = 0;
    this.synthGain.connect(this.master);
    this.trackGain.connect(this.master);
    this._noise = this._makeNoiseBuffer();
    this._startDrone();
    this._startBrook();
  }

  // 2s of looping white noise, reused for the brook and breath layers.
  _makeNoiseBuffer() {
    const len = this.ctx.sampleRate * 2;
    const buf = this.ctx.createBuffer(1, len, this.ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < len; i++) data[i] = Math.random() * 2 - 1;
    return buf;
  }

  _noiseSource() {
    const src = this.ctx.createBufferSource();
    src.buffer = this._noise; src.loop = true;
    return src;
  }

  // Warm low drone for depth beneath the garden.
  _startDrone() {
    const ctx = this.ctx;
    const g = ctx.createGain();
    g.gain.value = 0.07;
    const filt = ctx.createBiquadFilter();
    filt.type = "lowpass"; filt.frequency.value = 420;
    g.connect(filt); filt.connect(this.synthGain);
    [130.81, 196.0].forEach((f, i) => {
      const o = ctx.createOscillator();
      o.type = "sine"; o.frequency.value = f; o.detune.value = i ? 5 : -5;
      o.connect(g); o.start();
    });
    const lfo = ctx.createOscillator(); lfo.frequency.value = 0.06;
    const lfoGain = ctx.createGain(); lfoGain.gain.value = 0.03;
    lfo.connect(lfoGain); lfoGain.connect(g.gain); lfo.start();
  }

  // Continuous "trickling brook": bandpassed noise with a wandering filter and
  // a gentle amplitude shimmer so the water audibly babbles.
  _startBrook() {
    const ctx = this.ctx;
    const src = this._noiseSource();
    const bp = ctx.createBiquadFilter();
    bp.type = "bandpass"; bp.frequency.value = 1700; bp.Q.value = 0.6;
    const hp = ctx.createBiquadFilter();
    hp.type = "highpass"; hp.frequency.value = 600;
    const g = ctx.createGain(); g.gain.value = 0.16;
    src.connect(bp); bp.connect(hp); hp.connect(g); g.connect(this.synthGain);
    src.start();
    // wander the brook's brightness so it shimmers like moving water
    const lfo = ctx.createOscillator(); lfo.type = "sine"; lfo.frequency.value = 0.13;
    const lfoGain = ctx.createGain(); lfoGain.gain.value = 900;
    lfo.connect(lfoGain); lfoGain.connect(bp.frequency); lfo.start();
    // slow amplitude shimmer (the "trickle")
    const alfo = ctx.createOscillator(); alfo.type = "sine"; alfo.frequency.value = 0.5;
    const alfoGain = ctx.createGain(); alfoGain.gain.value = 0.05;
    alfo.connect(alfoGain); alfoGain.connect(g.gain); alfo.start();
  }

  _pan(node) {
    if (this.ctx.createStereoPanner) {
      const pan = this.ctx.createStereoPanner();
      pan.pan.value = (Math.random() * 2 - 1) * 0.55;
      node.connect(pan); pan.connect(this.synthGain);
    } else {
      node.connect(this.synthGain);
    }
  }

  // A single water droplet: quick descending chirp through a resonant bandpass.
  _droplet() {
    const ctx = this.ctx; if (!ctx) return;
    const now = ctx.currentTime;
    const o = ctx.createOscillator(); o.type = "sine";
    const top = 1300 + Math.random() * 900;
    o.frequency.setValueAtTime(top, now);
    o.frequency.exponentialRampToValueAtTime(top * 0.45, now + 0.07);
    const bp = ctx.createBiquadFilter(); bp.type = "bandpass";
    bp.frequency.value = top * 0.8; bp.Q.value = 6;
    const g = ctx.createGain();
    o.connect(bp); bp.connect(g);
    this._pan(g);
    g.gain.setValueAtTime(0.0001, now);
    g.gain.linearRampToValueAtTime(0.13 + Math.random() * 0.06, now + 0.006);
    g.gain.exponentialRampToValueAtTime(0.0001, now + 0.18);
    o.start(now); o.stop(now + 0.22);
  }

  // Koto pluck: bright triangle string in the pentatonic scale.
  _pluck() {
    const ctx = this.ctx; if (!ctx) return;
    const now = ctx.currentTime;
    const octave = Math.random() < 0.25 ? 0.5 : 1;
    const freq = PENTA[Math.floor(Math.random() * PENTA.length)] * octave;
    const o = ctx.createOscillator(); o.type = "triangle"; o.frequency.value = freq;
    const o2 = ctx.createOscillator(); o2.type = "sine"; o2.frequency.value = freq * 2; o2.detune.value = 4;
    const g2 = ctx.createGain(); g2.gain.value = 0.35; o2.connect(g2);
    const g = ctx.createGain();
    const filt = ctx.createBiquadFilter(); filt.type = "lowpass"; filt.frequency.value = 1900;
    o.connect(filt); g2.connect(filt); filt.connect(g);
    this._pan(g);
    g.gain.setValueAtTime(0.0001, now);
    g.gain.linearRampToValueAtTime(0.2, now + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, now + 2.6);
    o.start(now); o2.start(now); o.stop(now + 2.8); o2.stop(now + 2.8);
  }

  // A single breathy flute tone with a gentle attack, vibrato and airy breath.
  _fluteNote(freq, at, dur) {
    const ctx = this.ctx;
    const o = ctx.createOscillator(); o.type = "sine"; o.frequency.value = freq;
    const harm = ctx.createOscillator(); harm.type = "sine"; harm.frequency.value = freq * 2;
    const harmG = ctx.createGain(); harmG.gain.value = 0.12; harm.connect(harmG);
    // vibrato
    const vib = ctx.createOscillator(); vib.type = "sine"; vib.frequency.value = 5;
    const vibG = ctx.createGain(); vibG.gain.value = 6; // cents
    vib.connect(vibG); vibG.connect(o.detune);
    // breath noise shaped around the note
    const breath = this._noiseSource();
    const bbp = ctx.createBiquadFilter(); bbp.type = "bandpass"; bbp.frequency.value = freq * 2.2; bbp.Q.value = 2;
    const breathG = ctx.createGain(); breathG.gain.value = 0.02;
    breath.connect(bbp); bbp.connect(breathG);
    const g = ctx.createGain();
    o.connect(g); harmG.connect(g); breathG.connect(g);
    this._pan(g);
    const a = 0.45, r = 1.2;
    g.gain.setValueAtTime(0.0001, at);
    g.gain.linearRampToValueAtTime(0.17, at + a);
    g.gain.setValueAtTime(0.17, at + dur - r);
    g.gain.exponentialRampToValueAtTime(0.0001, at + dur);
    o.start(at); harm.start(at); vib.start(at); breath.start(at);
    const end = at + dur + 0.05;
    o.stop(end); harm.stop(end); vib.stop(end); breath.stop(end);
  }

  // Occasionally phrase 1–3 flute notes in the pentatonic scale.
  _flutePhrase() {
    const ctx = this.ctx; if (!ctx) return;
    let t = ctx.currentTime + 0.05;
    const notes = 1 + Math.floor(Math.random() * 3);
    for (let i = 0; i < notes; i++) {
      const freq = FLUTE_SCALE[Math.floor(Math.random() * FLUTE_SCALE.length)];
      const dur = 1.6 + Math.random() * 1.4;
      this._fluteNote(freq, t, dur);
      t += dur * (0.6 + Math.random() * 0.3);
    }
  }

  _startScheduler() {
    if (this._timers.length) return;
    const droplet = () => { this._droplet(); if (Math.random() < 0.4) setTimeout(() => this._droplet(), 120 + Math.random() * 200); };
    const schedule = (fn, min, span, chance) => {
      const run = () => {
        if (this.enabled && Math.random() < chance) fn();
        this._timers.push(setTimeout(run, min + Math.random() * span));
      };
      run();
    };
    schedule(droplet, 400, 1100, 1);            // trickling water droplets
    schedule(() => this._pluck(), 1700, 2200, 0.75); // koto plucks
    schedule(() => this._flutePhrase(), 5000, 5000, 0.85); // flute phrases
  }
  _stopScheduler() { this._timers.forEach(clearTimeout); this._timers = []; }

  // Build (or rebuild) the <audio> element + MediaElementSource for a custom track.
  _ensureAudioEl() {
    if (!this.trackUrl) return;
    if (this.audioEl && this.audioEl.dataset.src === this.trackUrl) return;
    // A MediaElementSource can only wrap one element, so recreate on url change.
    if (this.audioEl) { try { this.audioEl.pause(); } catch {} }
    if (this.mediaSrc) { try { this.mediaSrc.disconnect(); } catch {} }
    const a = new Audio();
    a.crossOrigin = "anonymous";
    a.loop = true; a.preload = "auto"; a.src = this.trackUrl;
    a.dataset.src = this.trackUrl;
    // Persist playback position so a full page reload resumes the Zen vibe
    // where it left off (in-app navigations already keep this element alive).
    a.addEventListener("loadedmetadata", () => {
      const pos = parseFloat(localStorage.getItem(KEY_POS));
      if (Number.isFinite(pos) && pos > 0 && a.duration && pos < a.duration - 0.5) {
        try { a.currentTime = pos; } catch {}
      }
    }, { once: true });
    a.addEventListener("timeupdate", () => {
      const now = Date.now();
      if (now - this._lastPosSave > 5000) {
        this._lastPosSave = now;
        try { localStorage.setItem(KEY_POS, String(a.currentTime)); } catch {}
      }
    });
    this.mediaSrc = this.ctx.createMediaElementSource(a);
    this.mediaSrc.connect(this.trackGain);
    this.audioEl = a;
  }

  // Route audio to the synth bed or the custom track based on trackUrl.
  _applyMode() {
    if (!this.ctx) return;
    const now = this.ctx.currentTime;
    if (this.trackUrl) {
      this.mode = "track";
      this._ensureAudioEl();
      this.synthGain.gain.setTargetAtTime(0, now, 0.2);
      this.trackGain.gain.setTargetAtTime(1, now, 0.2);
      this._stopScheduler();
      if (this.enabled && this.audioEl) { this.audioEl.play().catch(() => {}); }
    } else {
      this.mode = "synth";
      this.trackGain.gain.setTargetAtTime(0, now, 0.2);
      this.synthGain.gain.setTargetAtTime(1, now, 0.2);
      if (this.audioEl) { try { this.audioEl.pause(); } catch {} }
      if (this.enabled) this._startScheduler();
    }
  }

  _target() {
    // During a breathing session the master must be audibly open regardless of
    // whether ambient was enabled/ducked or the volume slider was low/zero.
    if (this._breathing) return Math.max(this.volume, 0.35);
    return this.enabled ? (this.ducked ? this.volume * this.duckLevel : this.volume) : 0;
  }
  _ramp(dur = FADE) {
    if (!this.master || !this.ctx) return;
    const now = this.ctx.currentTime;
    const g = this.master.gain;
    g.cancelScheduledValues(now);
    g.setValueAtTime(Math.max(0.0001, g.value), now);
    g.linearRampToValueAtTime(this._target(), now + dur);
  }

  async enable() {
    this._ensureCtx();
    if (!this.ctx) return;
    try { await this.ctx.resume(); } catch {}
    this.enabled = true; this._setPref(true);
    this._applyMode();
    this._ramp();
    this._emit();
  }

  disable() {
    this.enabled = false; this._setPref(false);
    this._ramp();
    setTimeout(() => {
      if (!this.enabled) {
        this._stopScheduler();
        if (this.audioEl) { try { this.audioEl.pause(); } catch {} }
        this.ctx && this.ctx.suspend().catch(() => {});
      }
    }, FADE * 1000 + 120);
    this._emit();
  }

  toggle() { this.enabled ? this.disable() : this.enable(); }

  setDuck(d) { this.ducked = d; if (this.enabled) this._ramp(); }

  // Set ambient volume (0–1), persisted. Quick ramp so the slider feels live.
  setVolume(v) {
    this.volume = Math.min(1, Math.max(0, v));
    this._setVol(this.volume);
    if (this.enabled) this._ramp(0.15);
  }

  // How far to duck during course/video playback (0–1 of base volume), persisted.
  setDuckLevel(v) {
    this.duckLevel = Math.min(1, Math.max(0, v));
    this._setDuck(this.duckLevel);
    if (this.enabled && this.ducked) this._ramp(0.15);
  }

  // Point Dojo Mode at a custom uploaded track (or null to use the synth bed).
  setTrack(url) {
    const next = url || null;
    if (next === this.trackUrl) return;
    this.trackUrl = next;
    if (this.ctx) this._applyMode();
  }

  // --- Secret Breathing session -------------------------------------------
  // Swap the koto bed for a calm rhythmic "breath" pad (inhale swell / exhale
  // fall) synced to a 12s cycle. Restores the prior ambient state on stop.
  startBreathing() {
    // Cancel any pending teardown (e.g. a rapid stop→start from React StrictMode
    // remounting the overlay) so it can't destroy the pad we're about to (re)build.
    if (this._breathTeardownTimer) { clearTimeout(this._breathTeardownTimer); this._breathTeardownTimer = null; }
    if (this._breathing) return;
    this._ensureCtx();
    if (!this.ctx) return;
    try { this.ctx.resume(); } catch {}
    this._teardownBreath();           // clear any leftover nodes from a prior session
    this._breathing = true;
    this._breathPrevEnabled = this.enabled;
    this._breathPrevDucked = this.ducked;
    this.ducked = false;             // never duck the breath session
    const now = this.ctx.currentTime;
    this._stopScheduler();
    this.synthGain.gain.setTargetAtTime(0, now, 0.4);
    this.trackGain.gain.setTargetAtTime(0, now, 0.4);
    if (this.audioEl) { try { this.audioEl.pause(); } catch {} }
    this.enabled = true;              // ensure the master is audible for the session
    this._buildBreathPad();
    this._ramp(1.5);
    this._breathCycle();
    this._emit();
  }

  _buildBreathPad() {
    const ctx = this.ctx;
    this._breathGain = ctx.createGain(); this._breathGain.gain.value = 0.0001;
    this._breathGain.connect(this.master);
    // airy breath — bandpassed noise whose brightness rises on the inhale
    const src = this._noiseSource();
    const bp = ctx.createBiquadFilter(); bp.type = "bandpass"; bp.frequency.value = 400; bp.Q.value = 0.7;
    const air = ctx.createGain(); air.gain.value = 0.6;
    src.connect(bp); bp.connect(air); air.connect(this._breathGain);
    src.start();
    // warm low swell under the breath
    const o = ctx.createOscillator(); o.type = "sine"; o.frequency.value = 110;
    const og = ctx.createGain(); og.gain.value = 0.22;
    o.connect(og); og.connect(this._breathGain); o.start();
    this._breathNoise = src; this._breathBp = bp; this._breathOsc = o;
  }

  // Schedule one 4s inhale / 4s hold / 4s exhale envelope, then repeat.
  _breathCycle() {
    if (!this._breathing || !this.ctx || !this._breathGain) return;
    const now = this.ctx.currentTime;
    const g = this._breathGain.gain;
    const f = this._breathBp.frequency;
    g.cancelScheduledValues(now); f.cancelScheduledValues(now);
    g.setValueAtTime(Math.max(0.0001, g.value), now);
    f.setValueAtTime(380, now);
    g.linearRampToValueAtTime(0.9, now + 4);      // inhale
    f.linearRampToValueAtTime(1500, now + 4);
    g.setValueAtTime(0.9, now + 8);               // hold
    f.setValueAtTime(1500, now + 8);
    g.linearRampToValueAtTime(0.04, now + 12);    // exhale
    f.linearRampToValueAtTime(380, now + 12);
    this._breathTimer = setTimeout(() => this._breathCycle(), 12000);
  }

  stopBreathing() {
    if (!this._breathing && !this._breathGain) return;
    this._breathing = false;
    if (this._breathTimer) { clearTimeout(this._breathTimer); this._breathTimer = null; }
    if (this.ctx && this._breathGain) {
      const now = this.ctx.currentTime;
      this._breathGain.gain.cancelScheduledValues(now);
      this._breathGain.gain.setValueAtTime(Math.max(0.0001, this._breathGain.gain.value), now);
      this._breathGain.gain.linearRampToValueAtTime(0.0001, now + 1);
    }
    // Delay teardown for the fade; a subsequent startBreathing cancels this timer.
    if (this._breathTeardownTimer) clearTimeout(this._breathTeardownTimer);
    this._breathTeardownTimer = setTimeout(() => { this._breathTeardownTimer = null; this._teardownBreath(); }, 1100);
    // Restore the ambient bed to whatever it was before the session.
    this.ducked = this._breathPrevDucked || false;
    this.enabled = this._breathPrevEnabled;
    if (this.enabled) this._applyMode();
    this._ramp(1.5);
    this._emit();
  }

  _teardownBreath() {
    try { this._breathNoise && this._breathNoise.stop(); } catch {}
    try { this._breathOsc && this._breathOsc.stop(); } catch {}
    try { this._breathGain && this._breathGain.disconnect(); } catch {}
    this._breathNoise = this._breathOsc = this._breathGain = this._breathBp = null;
  }

  // If the user previously enabled Dojo Mode, resume it on their first gesture
  // (browsers block audio until a user interaction).
  armAutoResume() {
    if (this._resume || this.enabled || !this.getPref()) return;
    this._resume = () => {
      document.removeEventListener("pointerdown", this._resume);
      document.removeEventListener("keydown", this._resume);
      this._resume = null;
      this.enable();
    };
    document.addEventListener("pointerdown", this._resume);
    document.addEventListener("keydown", this._resume);
  }
}

export const dojo = new DojoAudio();
// Exposed for automated tests (dev/preview builds only) to assert audio state.
if (typeof window !== "undefined" && process.env.NODE_ENV !== "production") window.__dojoAudio = dojo;

export function useDojo() {
  const [enabled, setEnabled] = useState(dojo.enabled);
  const [volume, setVol] = useState(dojo.volume);
  const [duckLevel, setDL] = useState(dojo.duckLevel);
  useEffect(() => dojo.subscribe(setEnabled), []);
  return {
    enabled,
    volume,
    duckLevel,
    enable: () => dojo.enable(),
    disable: () => dojo.disable(),
    toggle: () => dojo.toggle(),
    setVolume: (v) => { dojo.setVolume(v); setVol(dojo.volume); },
    setDuckLevel: (v) => { dojo.setDuckLevel(v); setDL(dojo.duckLevel); },
  };
}
